/**
 *
 * Create or Update Recipe Page
 *
 */
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  notification,
  PageHeader,
  Space,
} from 'antd';
import { useSelector } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router';

import { RouteConstants } from 'app/routes';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { MainContainer } from 'app/components/LayoutComponents/LayoutComponents';
import RecipeCategorySelect from 'app/components/Select/RecipeCategorySelect/RecipeCategorySelect';
import { selectUser } from 'app/containers/Authorization/slice/selectors';
import CultureSelect from 'app/components/Select/CultureSelect/CultureSelect';
import RecipeResourceTable from '../../parts/RecipeResourceTable/RecipeResourceTable';
import NumberInput from 'app/components/NumberInput/NumberInput';
import { ICreateRecipePayload, IRecipeResource } from '../../types';
import { formatUserName, getInteger } from 'utils/helpers';
import {
  useCreateRecipeMutation,
  useGetRecipeByIdQuery,
  useUpdateRecipeMutation,
} from 'app/api';
import { handleError } from 'app/containers/Global/slice/helpers';
import { getEditRecipeFormInitialValues } from '../../helpers';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';

interface IReactRouterParams {
  recipeId: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const CreateOrUpdateRecipe: FC<IProps> = ({
  history,
  match: { path, params },
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { mobileOrTablet } = useResponsiveQuery();

  const recipeId = Number(params.recipeId);
  const isEditPage = path === RouteConstants.recipe.edit && recipeId;

  const envId = useSelector(selectSelectedEnvId) as number;
  const user = useSelector(selectUser);

  const { data: recipe, isFetching } = useGetRecipeByIdQuery(
    { recipeId, envId },
    { skip: !envId || !recipeId },
  );

  const recipeCreator = recipe
    ? formatUserName(recipe.createdBy)
    : formatUserName(user);

  const [
    createRecipe,
    { isLoading: isCreateRecipeLoading },
  ] = useCreateRecipeMutation();

  const [
    updateRecipe,
    { isLoading: isUpdateRecipeLoading },
  ] = useUpdateRecipeMutation();
  const [selectedResources, setSelectedResources] = useState<IRecipeResource[]>(
    [],
  );

  const isSubmitLoading = isCreateRecipeLoading || isUpdateRecipeLoading;

  // Set edit data on open
  useEffect(() => {
    if (isEditPage && recipe) {
      const values = getEditRecipeFormInitialValues(recipe);

      form.setFieldsValue(values);

      const resources: IRecipeResource[] =
        recipe?.resources.map(resource => ({
          ...resource,
        })) || [];

      setSelectedResources(resources);
    }
  }, [recipe, isEditPage, form]);

  const goBackToList = () => {
    history.push(
      generatePath(RouteConstants.recipe.index, {
        envId,
      }),
    );
  };

  const onFormSubmit = async (values: {
    name: string;
    categoryId: number;
    cultureId: number;
    bbchFrom: string;
    bbchTo: string;
    description: string;
  }) => {
    try {
      const {
        bbchFrom,
        bbchTo,
        cultureId,
        description,
        name,
        categoryId,
      } = values;

      const resources = selectedResources.map(resource => ({
        id: resource.id,
        norm: resource.norm,
      }));

      const payload: ICreateRecipePayload = {
        envId,
        bbchFrom: getInteger(bbchFrom) || 0,
        bbchTo: getInteger(bbchTo) || 0,
        cultureId,
        description,
        name,
        categoryId,
        resources,
      };
      if (isEditPage && recipeId) {
        await updateRecipe({ ...payload, recipeId }).unwrap();
        notification.success({
          message: t('Recipe.SuccessfullyUpdated', {
            defaultValue: 'Recipe "{{name}}" successfully updated',
            name,
          }),
          placement: 'bottomRight',
        });
      } else {
        await createRecipe(payload).unwrap();
        notification.success({
          message: t('Recipe.SuccessfullyCreated', {
            defaultValue: 'Recipe "{{name}}" successfully created',
            name,
          }),
          placement: 'bottomRight',
        });
      }
      goBackToList();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      <PageHeader
        onBack={goBackToList}
        className="page-header"
        title={
          isEditPage
            ? t('Recipe.EditRecipe', 'Edit recipe')
            : t('Recipe.CreateRecipe', 'Create recipe')
        }
      />
      <MainContainer>
        <Card
          loading={isFetching}
          style={{
            minHeight: mobileOrTablet ? 'unset' : 'calc(100vh - 190px)',
            maxHeight: mobileOrTablet ? 'unset' : 'calc(100vh - 170px)',
            overflow: 'auto',
            width: '100%',
          }}
          title={t('Recipe.RecipeInformation', 'Recipe information')}
          extra={[
            <Button
              type="primary"
              form="createRecipeForm"
              htmlType="submit"
              key="submit"
              loading={isSubmitLoading}
            >
              {t('Common.Save', 'Save')}
            </Button>,
          ]}
        >
          <Form
            form={form}
            requiredMark={true}
            onFinish={onFormSubmit}
            id="createRecipeForm"
          >
            <Descriptions bordered column={{ lg: 2, md: 1, xs: 1, sm: 1 }}>
              <Descriptions.Item
                label={t('Recipe.Title', 'Title')}
                contentStyle={{ padding: '24px 16px 0px' }}
              >
                <Form.Item name="name" rules={[{ required: true }]}>
                  <Input
                    placeholder={t('Recipe.Title', 'Title')}
                    style={{ width: '217px' }}
                  />
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item label={t('Recipe.Creator', 'Creator')}>
                {recipeCreator}
              </Descriptions.Item>

              <Descriptions.Item
                label={t('Recipe.Category', 'Category')}
                span={2}
                contentStyle={{ padding: '24px 16px 0px' }}
              >
                <Form.Item name="categoryId" rules={[{ required: true }]}>
                  <RecipeCategorySelect style={{ width: '217px' }} />
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item
                label={t('Common.Culture', 'Culture')}
                contentStyle={{ padding: '24px 16px 0px' }}
              >
                <Form.Item name="cultureId" rules={[{ required: true }]}>
                  <CultureSelect style={{ width: '217px' }} />
                </Form.Item>
              </Descriptions.Item>

              <Descriptions.Item
                span={2}
                label={t('Recipe.RecommendedBBCH', 'Recommended BBCH')}
                contentStyle={{ padding: '24px 16px 0px' }}
              >
                <Space>
                  <Form.Item name="bbchFrom" rules={[{ required: true }]}>
                    <NumberInput placeholder={t('Recipe.From', 'From')} />
                  </Form.Item>

                  <Form.Item name="bbchTo" rules={[{ required: true }]}>
                    <NumberInput
                      placeholder={t('Recipe.To', 'To')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Space>
              </Descriptions.Item>

              <Descriptions.Item
                label={t('Recipe.Description', 'Recipe description')}
                contentStyle={{ padding: '24px 16px 0px' }}
              >
                <Form.Item name="description" rules={[{ required: true }]}>
                  <Input.TextArea
                    rows={4}
                    placeholder={t('Recipe.Description', 'Recipe description')}
                  />
                </Form.Item>
              </Descriptions.Item>
            </Descriptions>
          </Form>
          <RecipeResourceTable
            isEditable={true}
            selectedResources={selectedResources}
            setSelectedResources={setSelectedResources}
          />
        </Card>
      </MainContainer>
    </>
  );
};

export default CreateOrUpdateRecipe;
